import React from 'react';
import { Screening } from '../../types';
import { calculateAvailableSeats } from '../../lib/seatUtils';
import styles from '../../styles/BookingModal.module.css';

interface SeatSelectionProps {
  selectedScreening: Screening | null;
  seatCounts: { silver: string; gold: string; diamond: string };
  setSeatCounts: React.Dispatch<React.SetStateAction<{ silver: string; gold: string; diamond: string }>>;
  seatPrices: { silver: number; gold: number; diamond: number };
  session: any;
  clearSeats: () => void;
  splitEnabled?: boolean;
}

const SeatSelection = ({ 
  selectedScreening, 
  seatCounts, 
  setSeatCounts, 
  seatPrices,
  session,
  clearSeats,
  splitEnabled = true,
}: SeatSelectionProps) => {
  // Descriptions for each seat type
  const seatDescriptions: Record<string, string> = {
    silver: 'Front row seats',
    gold: 'Middle row seats',
    diamond: 'Last three rows at the back'
  };

  // Calculate available seats for each type (only when seat split is loaded)
  const hasSplit = splitEnabled && !!selectedScreening?.seat_splitting;
  const availableSeats = hasSplit
    ? calculateAvailableSeats(selectedScreening?.seat_splitting)
    : { silver: 0, gold: 0, diamond: 0, total: 0 };

  const handleSeatCountChange = (type: string, value: string) => {
    const sanitizedValue = value.replace(/\D/g, '');
    const parsedValue = parseInt(sanitizedValue) || 0;
    const nextValue = hasSplit
      ? Math.min(parsedValue, availableSeats[type] || 0).toString()
      : sanitizedValue; // allow typing freely before availability loads

    setSeatCounts(prevCounts => ({
      ...prevCounts,
      [type]: nextValue === '0' ? '' : nextValue
    }));
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>Select Seat Type</label>
      {splitEnabled ? (
        <div className={styles.seatTypeOptions}>
          {['silver', 'gold', 'diamond'].map((type) => (
            <div key={type} className={styles.seatOption}>
              <label htmlFor={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)} (€{seatPrices[type]} each)
                <span className={styles.tooltip}>
                  <span className={styles.questionMark}> : </span>
                  <span className={styles.tooltiptext}>
                    {seatDescriptions[type]}
                  </span>
                </span>
                {selectedScreening && (
                  <span className={styles.seatAvailability}>
                    {" "}
                    ({availableSeats[type]} available)
                  </span>
                )}
              </label>
              <div className={styles.formGroup}>
                <input
                  type="number"
                  min="0"
                  max={hasSplit ? availableSeats[type] : undefined}
                  value={seatCounts[type]}
                  onChange={(e) => handleSeatCountChange(type, e.target.value)}
                  className={styles.input}
                  placeholder={hasSplit ? `0-${availableSeats[type]} seats` : 'Enter seats'}
                  disabled={!session || !selectedScreening || (hasSplit && availableSeats[type] <= 0)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.formGroup}>
          <label htmlFor="tickets">Tickets</label>
          <input
            id="tickets"
            type="number"
            min="0"
            max={hasSplit ? availableSeats.silver : undefined}
            value={seatCounts.silver}
            onChange={(e) => handleSeatCountChange('silver', e.target.value)}
            className={styles.input}
            placeholder={hasSplit ? `0-${availableSeats.silver} seats` : 'Enter seats'}
            disabled={!session || !selectedScreening || (hasSplit && availableSeats.silver <= 0)}
          />
        </div>
      )}
      {session && selectedScreening && (
        <div className={styles.seatSummary}>
          {/* <p>Total available seats: {availableSeats.total}</p> */}
          <button onClick={clearSeats} className={`${styles.button} ${styles.secondaryButton}`}>
            Clear Seats
          </button>
        </div>
      )}
    </div>
  );
};

export default SeatSelection;
